import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import RatingStars from "../../ui/RatingStars";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { motion } from "framer-motion";
import * as SM from "./Farms.mobile.styled";
import { Button } from "../../ui/Button";
import { fadeUp, staggerContainer, useMotion } from "../../ui/motion";

const FarmerBadge = ({ name, imageUrl }) => {
  if (!name) {
    return null;
  }

  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <SM.FarmerBadge>
      {imageUrl ? (
        <SM.FarmerAvatar src={imageUrl} alt={name} loading="lazy" />
      ) : (
        <SM.FarmerInitials aria-hidden="true">{initials}</SM.FarmerInitials>
      )}
      <SM.FarmerName>{name}</SM.FarmerName>
    </SM.FarmerBadge>
  );
};

const FarmCardList = ({
  isLoading,
  filteredFarms,
  displayedFarms,
  savedFarmIds,
  onToggleWishlist,
  onResetFilters,
  canLoadMore,
  isLoadingMore,
  onLoadMore,
  deliveryLabelMap,
  deliveryWindowOptions,
  ctaLabels,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reduce } = useMotion();

  if (isLoading) {
    return (
      <SM.CardList aria-live="polite" aria-busy="true" aria-label={t("common.loading", { defaultValue: "Loading farms" })}>
        {Array.from({
          length: Math.max(3, filteredFarms.length || 0),
        }).map((_, index) => (
          <SM.SkeletonCard
            key={`initial-skeleton-${index}`}
            data-testid="farm-skeleton"
          >
            <SM.SkeletonMedia />
            <SM.SkeletonBody>
              <SM.SkeletonLine $width={70} />
              <SM.SkeletonLine $width={55} />
              <SM.SkeletonLine $width={90} />
              <SM.SkeletonLine $width={45} />
            </SM.SkeletonBody>
          </SM.SkeletonCard>
        ))}
      </SM.CardList>
    );
  }

  if (filteredFarms.length === 0) {
    return (
      <SM.EmptyState>
        <SM.EmptyTitle>
          {t("farms.empty.title", {
            defaultValue: "No farms match these filters",
          })}
        </SM.EmptyTitle>
        <p>
          {t("farms.empty.copy", {
            defaultValue:
              "Try adjusting the filters or broadening your delivery window.",
          })}
        </p>
        <SM.FilterButton
          type="button"
          variant="outline"
          fullWidth
          onClick={onResetFilters}
        >
          {t("farms.empty.clear", { defaultValue: "Clear filters" })}
        </SM.FilterButton>
      </SM.EmptyState>
    );
  }

  return (
    <>
      <SM.CardList
        as={motion.div}
        aria-live="polite"
        variants={staggerContainer(reduce, 0.06)}
        initial="hidden"
        animate="show"
      >
        {displayedFarms.map((farm) => (
          <motion.div key={farm.id} variants={fadeUp(reduce)}>
          <SM.FarmCard data-testid="farm-card">
            <SM.FarmMedia>
              <source
                srcSet={`${farm.image} 768w`}
                media="(min-width: 480px)"
              />
              <SM.FarmImage
                src={farm.image}
                alt={farm.name}
                loading="lazy"
                sizes="(max-width: 768px) 100vw, 640px"
              />
              <SM.WishlistButton
                type="button"
                onClick={(event) => onToggleWishlist(event, farm.id)}
                $saved={savedFarmIds.has(farm.id)}
                aria-label={savedFarmIds.has(farm.id) ? t("farms.wishlist.remove", { defaultValue: "Remove from wishlist" }) : t("farms.wishlist.add", { defaultValue: "Save to wishlist" })}
              >
                {savedFarmIds.has(farm.id) ? <FaHeart /> : <FaRegHeart />}
              </SM.WishlistButton>
              <FarmerBadge
                name={farm.ownerFirstName || farm.ownerName}
                imageUrl={farm.ownerProfilePictureUrl}
              />
            </SM.FarmMedia>
            <SM.FarmContent>
              <SM.FarmHeader>
                <SM.FarmName>{farm.name}</SM.FarmName>
                <SM.FarmMeta>
                  {farm.location} •{" "}
                  {deliveryLabelMap[farm.deliveryWindow] ??
                    deliveryWindowOptions[0].label}
                </SM.FarmMeta>
                {farm.reviewCount > 0 ? (
                  <SM.FarmRating>
                    <RatingStars
                      rating={farm.averageRating}
                      count={farm.reviewCount}
                      size="0.85rem"
                    />
                  </SM.FarmRating>
                ) : null}
              </SM.FarmHeader>
              {farm.tags.length ? (
                <SM.TagList>
                  {farm.tags.map((tag) => (
                    <SM.Tag key={`${farm.id}-${tag}`}>{tag}</SM.Tag>
                  ))}
                </SM.TagList>
              ) : null}
              <SM.Description>{farm.description}</SM.Description>
              <SM.Actions>
                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  onClick={() => navigate(`/farms/${farm.id}`)}
                >
                  {ctaLabels[farm.primaryAction] ?? ctaLabels.viewFarm}
                </Button>
                {farm.secondaryAction ? (
                  <SM.SecondaryButton
                    type="button"
                    variant="outline"
                    fullWidth
                    onClick={() =>
                      navigate(`/contact?farm=${encodeURIComponent(farm.id)}`)
                    }
                  >
                    {ctaLabels[farm.secondaryAction] ?? ctaLabels.contact}
                  </SM.SecondaryButton>
                ) : null}
              </SM.Actions>
            </SM.FarmContent>
          </SM.FarmCard>
          </motion.div>
        ))}

        {isLoadingMore
          ? Array.from({
              length: Math.max(
                1,
                Math.min(3, filteredFarms.length - displayedFarms.length)
              ),
            }).map((_, index) => (
              <SM.SkeletonCard
                key={`loadmore-skeleton-${index}`}
                data-testid="farm-skeleton"
              >
                <SM.SkeletonMedia />
                <SM.SkeletonBody>
                  <SM.SkeletonLine $width={70} />
                  <SM.SkeletonLine $width={50} />
                  <SM.SkeletonLine $width={85} />
                </SM.SkeletonBody>
              </SM.SkeletonCard>
            ))
          : null}
      </SM.CardList>

      {canLoadMore ? (
        <SM.LoadMoreWrap>
          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore
              ? t("farms.loading_more", { defaultValue: "Loading farms\u2026" })
              : t("farms.load_more", { defaultValue: "Load more farms" })}
          </Button>
        </SM.LoadMoreWrap>
      ) : null}
    </>
  );
};

export default FarmCardList;
